import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PinDropIcon from '@mui/icons-material/PinDrop';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MapIcon from '@mui/icons-material/Map';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { API_BASE_URL } from '../../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import { usePermissions } from '../../../context/PermissionContext';
import BackButton from '../../../components/BackButton';
import { LEVEL_NAMES } from '../../../constants/userLevels';

const StateHeadsList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { can } = usePermissions();
  const [userId, setUserId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  // Confirmation dialog for status change
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [targetUserForStatus, setTargetUserForStatus] = useState(null);

  // Dialog for displaying lists (States, Districts, Villages, Pincodes)
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listDialogTitle, setListDialogTitle] = useState('');
  const [listDialogItems, setListDialogItems] = useState([]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.userId || '');
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, []);

  useEffect(() => {
    fetchStateHeads();
  }, []);

  const fetchStateHeads = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/users/by-level`, {
        params: { level: 2 },
      });
      const data = (Array.isArray(response.data) ? response.data : []).map((u, index) => ({
        ...u,
        id: u._id || index,
      }));
      setUsers(data);
    } catch (err) {
      console.error('Error fetching state heads:', err);
      setError((err && err.response && err.response.data && err.response.data.message) ? err.response.data.message : `Error fetching ${LEVEL_NAMES[2]}s data.`);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    if (!can('users.stateHeads.changeStatus')) return;
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const token = sessionStorage.getItem('token');

      await axios.patch(`${API_BASE_URL}/users/update-status/${id}`, {
        status: newStatus,
        requesterId: userId,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSnackbarMessage(`${LEVEL_NAMES[2]} ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchStateHeads();
    } catch (err) {
      console.error('Error updating status:', err);
      const errMsg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Error updating status';
      setSnackbarMessage(errMsg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleOpenConfirmDialog = (row) => {
    if (!can('users.stateHeads.changeStatus')) return;
    const currentStatus = row.status || 'active';
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const name = `${row.firstName || ''} ${row.lastName || ''}`.trim() || `this ${LEVEL_NAMES[2]}`;
    setTargetUserForStatus({
      id: row.id || row._id,
      name,
      currentStatus,
      newStatus,
    });
    setConfirmDialogOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!targetUserForStatus) return;
    const { id, currentStatus } = targetUserForStatus;
    setConfirmDialogOpen(false);
    await handleStatusToggle(id, currentStatus);
    setTargetUserForStatus(null);
  };

  const handleExportCSV = async () => {
    if (!can('users.stateHeads.export')) return;
    try {
      if (userId) {
        const token = sessionStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/users/export-csv/${userId}`, {
          params: {
            level: 2,
            status: filterStatus,
            search: searchText,
          },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `state_heads_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }
    } catch (err) {
      console.error('Backend CSV export error, fallback to client side CSV generation:', err);
    }

    if (!filteredUsers || filteredUsers.length === 0) {
      setSnackbarMessage('No data available to export');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const headers = [`${LEVEL_NAMES[2]} ID`, 'First Name', 'Last Name', 'Mobile Number', 'Email', 'Address', 'Status', 'States', 'Districts', 'Constituencies', 'Pincodes'];
    const csvRows = [headers.join(',')];

    filteredUsers.forEach(u => {
      const shId = u.stateHeadId || (u._id ? `SH-${String(u._id).slice(-6).toUpperCase()}` : 'N/A');
      const mobile = u.mobileNumber || u.phoneNumber || '';
      const states = (u.states || [u.stateCode || 'Kerala']).join(';');
      const districts = (u.districts || [u.district || '']).filter(Boolean).join(';');
      const villages = (u.villages || [u.city || '']).filter(Boolean).join(';');
      const pincodes = (u.pincodes || [u.pinCode ? String(u.pinCode) : '']).filter(Boolean).join(';');
      
      const row = [
        `"${shId}"`,
        `"${u.firstName || ''}"`,
        `"${u.lastName || ''}"`,
        `"${mobile}"`,
        `"${u.email || ''}"`,
        `"${(u.address || '').replace(/"/g, '""')}"`,
        `"${u.status || 'active'}"`,
        `"${states}"`,
        `"${districts}"`,
        `"${villages}"`,
        `"${pincodes}"`,
      ];
      csvRows.push(row.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `state_heads_report_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleOpenListDialog = (title, items) => {
    setListDialogTitle(title);
    setListDialogItems(Array.isArray(items) ? items : []);
    setListDialogOpen(true);
  };

  const handleCloseListDialog = () => {
    setListDialogOpen(false);
    setListDialogTitle('');
    setListDialogItems([]);
  };

  const filteredUsers = users.filter((u) => {
    if (filterStatus && (u.status || 'active') !== filterStatus) return false;
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      (u.stateHeadId && String(u.stateHeadId).toLowerCase().includes(q)) ||
      (u.firstName && u.firstName.toLowerCase().includes(q)) ||
      (u.lastName && u.lastName.toLowerCase().includes(q)) ||
      (u.mobileNumber && u.mobileNumber.includes(searchText)) ||
      (u.phoneNumber && u.phoneNumber.includes(searchText)) ||
      (u.email && u.email.toLowerCase().includes(q)) ||
      (u.address && u.address.toLowerCase().includes(q))
    );
  });

  const columns = [
    {
      field: 'stateHeadId',
      headerName: `${LEVEL_NAMES[2]} ID`,
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
          {params.value || (params.row._id ? `SH-${String(params.row._id).slice(-6).toUpperCase()}` : 'N/A')}
        </Typography>
      ),
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'mobileNumber',
      headerName: 'Mobile Number',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || params.row.phoneNumber || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 500 }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      renderCell: (params) => {
        const currentStatus = params.row.status || 'active';
        if (can('users.stateHeads.changeStatus')) {
          return (
            <Button
              variant="contained"
              size="small"
              color={currentStatus === 'active' ? 'secondary' : 'primary'}
              onClick={() => handleOpenConfirmDialog(params.row)}
              sx={{ fontWeight: 700, height: 32, my: 'auto' }}
            >
              {currentStatus === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          );
        }
        return (
          <Chip
            label={currentStatus === 'active' ? 'Active' : 'Inactive'}
            color={currentStatus === 'active' ? 'success' : 'error'}
            size="small"
            sx={{ fontWeight: 700, my: 'auto' }}
          />
        );
      },
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#475569', whiteSpace: 'normal', lineHeight: 1.3 }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'states',
      headerName: 'List of STATE',
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.states || (params.row.stateCode ? [params.row.stateCode] : ['Kerala']);
        return (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<MapIcon style={{ fontSize: 14 }} />}
            onClick={() => handleOpenListDialog(`States for ${params.row.firstName} ${params.row.lastName || ''}`, list)}
            sx={{ height: 32, my: 'auto', textTransform: 'none', fontWeight: 600 }}
          >
            {list.length > 0 ? `States (${list.length})` : 'None'}
          </Button>
        );
      },
    },
    {
      field: 'districts',
      headerName: 'List of DISTRICT',
      width: 170,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.districts || (params.row.district ? [params.row.district] : []);
        return (
          <Button
            size="small"
            variant="outlined"
            color="info"
            startIcon={<LocationCityIcon style={{ fontSize: 14 }} />}
            onClick={() => handleOpenListDialog(`Districts for ${params.row.firstName} ${params.row.lastName || ''}`, list)}
            sx={{ height: 32, my: 'auto', textTransform: 'none', fontWeight: 600 }}
          >
            {list.length > 0 ? `Districts (${list.length})` : 'None'}
          </Button>
        );
      },
    },
    {
      field: 'villages',
      headerName: 'List of CONSTITUENCY',
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.villages || (params.row.city ? [params.row.city] : []);
        return (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<LocationOnIcon style={{ fontSize: 14 }} />}
            onClick={() => handleOpenListDialog(`Constituencies for ${params.row.firstName} ${params.row.lastName || ''}`, list)}
            sx={{ height: 32, my: 'auto', textTransform: 'none', fontWeight: 600 }}
          >
            {list.length > 0 ? `Constituencies (${list.length})` : 'None'}
          </Button>
        );
      },
    },
    {
      field: 'pincodes',
      headerName: "List of PINCODE's",
      width: 170,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.pincodes || (params.row.pinCode ? [String(params.row.pinCode)] : []);
        return (
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<PinDropIcon style={{ fontSize: 14 }} />}
            onClick={() => handleOpenListDialog(`Pincodes for ${params.row.firstName} ${params.row.lastName || ''}`, list)}
            sx={{ height: 32, my: 'auto', textTransform: 'none', fontWeight: 600 }}
          >
            {list.length > 0 ? `Pincodes (${list.length})` : 'None'}
          </Button>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <BackButton />
      <br /><br />

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Typography variant="h5" color="primary" sx={{ fontWeight: 800, mb: 0.5 }}>
          {LEVEL_NAMES[2]}s
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          List of all {LEVEL_NAMES[2]} users and their assigned geographic jurisdictions
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Controls: Search, Status Filter, Export CSV */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder={`Search by ${LEVEL_NAMES[2]} ID, Name, Mobile, Email, or Address...`}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {can('users.stateHeads.export') && (
            <Grid item xs={6} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ fontWeight: 700, height: 40 }}
              >
                Export to CSV
              </Button>
            </Grid>
          )}
        </Grid>

        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            rowHeight={60}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </Paper>

      {/* Dialog for displaying list items */}
      <Dialog open={listDialogOpen} onClose={handleCloseListDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>
          {listDialogTitle}
        </DialogTitle>
        <DialogContent dividers>
          {listDialogItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No items assigned.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1 }}>
              {listDialogItems.map((item, idx) => (
                <Chip key={idx} label={String(item)} color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseListDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Status Change */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
          setTargetUserForStatus(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirm Status Change
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Are you sure you want to {targetUserForStatus?.newStatus === 'active' ? 'activate' : 'deactivate'}{' '}
            <strong>{targetUserForStatus?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false);
              setTargetUserForStatus(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStatusChange}
            variant="contained"
            color={targetUserForStatus?.newStatus === 'active' ? 'primary' : 'error'}
          >
            {targetUserForStatus?.newStatus === 'active' ? 'Activate' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StateHeadsList;
